import { Box, Typography } from "@mui/material"
import type { ReactElement } from "react"
import { useState } from "react"
import { useFormContext } from "react-hook-form"

import useEquivalentProductGroups from "../hooks/useEquivalentProductGroups"

import type { EquivalentProductGroupFormValues } from "./equivalentProductGroupSchema"

import ValidationAdornment from "@/components/common/Autocomplete/ValidationAdornment"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import { EquivalencyType } from "@/graphql/codegen/graphql"
import useDuplicateByFieldValidation from "@/hooks/useDuplicateByFieldValidation"
import useDebounce from "@/utils/useDebounce"

interface EquivalentProductGroupFormProps {
  isEditMode?: boolean
}

const MAX_NOTE_LENGTH = 2048

const EQUIVALENT_TYPE_OPTIONS = [
  { value: EquivalencyType.Chemical, label: "Chemical" },
  { value: EquivalencyType.Functional, label: "Functional" },
]

const EquivalentProductGroupForm = ({ isEditMode = false }: EquivalentProductGroupFormProps = {}): ReactElement => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<EquivalentProductGroupFormValues>()

  const [query, setQuery] = useState("")
  const debouncedName = useDebounce(query, DEBOUNCE_DELAY_MS)
  const shouldQuery = debouncedName.length >= 2
  const notes = watch("notes") || ""

  const { groupsList, groupsListFetching } = useEquivalentProductGroups({
    query: debouncedName,
    pause: !shouldQuery,
  })

  useDuplicateByFieldValidation({
    query,
    list: groupsList,
    fieldName: "name",
    fieldLabel: "name",
    entityName: "Equivalency group",
  })

  const handleNameChange = (value: string) => {
    setQuery(value)
  }

  const characterCount = notes.length

  return (
    <Box className='space-y-6'>
      <FormTextField
        control={control}
        name='name'
        label='Group Name'
        placeholder='Input Name'
        required
        onChange={handleNameChange}
        slotProps={{
          input: {
            endAdornment: query ? <ValidationAdornment loading={groupsListFetching} error={errors.name} /> : undefined,
          },
        }}
        data-testid='equivalent-group-name-input'
      />

      <FormSelect
        control={control}
        name='type'
        label='Equivalent Type'
        placeholder='Select One'
        required
        options={EQUIVALENT_TYPE_OPTIONS}
        disabled={isEditMode}
        data-testid='equivalent-type-select'
      />

      <Box>
        <FormTextField
          control={control}
          name='notes'
          label='Group Note'
          placeholder='Add a note'
          multiline
          rows={4}
          data-testid='equivalent-group-note-input'
        />
        <Typography variant='caption' className='mt-1 text-gray-600' data-testid='note-character-counter'>
          {characterCount}/{MAX_NOTE_LENGTH} characters
        </Typography>
      </Box>
    </Box>
  )
}

export default EquivalentProductGroupForm
